import { useParams, useNavigate } from "react-router";
import { useEffect, useState } from "react";

import ClosableCard from "@/components/closable-card";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Trash2, Edit2, Check, X } from "lucide-react";
import Block from "@/components/documents/block";
import AddBlockDropdown, {
  type BlockType,
} from "@/components/documents/add-block-dropdown";
import { type Document } from "@/zero/schema";
import type {
  FeatureBlock,
  PointBlock,
  MultiPointBlock,
  RemoteGeoJSONBlock,
} from "@/blocks/geojson.blocks";
import { useRoom } from "@/hooks/use-room";
import Sidebar from "@/components/layout-sidebar";
import type { RouteBlock } from "@/blocks/routes.blocks";

export default function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentDocument,
    currentBlockTree: tree,
    setCurrentDocument,
    docs,
  } = useRoom();
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState("");

  useEffect(() => {
    if (id && id !== currentDocument) {
      setCurrentDocument(id);
    }
  }, [id, currentDocument, setCurrentDocument]);

  function handleCreate(document: Document, blockType: BlockType) {
    if (!docs) return;

    switch (blockType) {
      case "feature":
        // For now, create a feature without geometry reference
        // We'll enhance this later when we add child manipulation methods
        docs.appendBlock(document, {
          type: "feature",
          attributes: {
            properties: {},
          },
          content: [],
          documentId: document.id,
          deleted: false,
        } satisfies Omit<FeatureBlock, "id">);
        break;

      case "point":
        docs.appendBlock(document, {
          type: "point",
          attributes: {
            coordinates: [5, 52], // Default coordinates [longitude, latitude]
          },
          content: [],
          documentId: document.id,
          deleted: false,
        } satisfies Omit<PointBlock, "id">);
        break;

      case "multipoint":
        docs.appendBlock(document, {
          type: "multipoint",
          attributes: {
            coordinates: [
              [5, 52],
              [6, 53],
            ], // Default multiple coordinates
          },
          content: [],
          documentId: document.id,
          deleted: false,
        } satisfies Omit<MultiPointBlock, "id">);
        break;

      case "route":
        docs.appendBlock(document, {
          type: "route",
          attributes: {
            mode: "unspecified",
          },
          content: [],
          documentId: document.id,
          deleted: false,
        } satisfies Omit<RouteBlock, "id">);
        break;

      case "remotegeojson":
        docs.appendBlock(document, {
          type: "remotegeojson",
          attributes: {
            url: "",
          },
          content: [],
          documentId: document.id,
          deleted: false,
        } satisfies Omit<RemoteGeoJSONBlock, "id">);
        break;

      default:
        console.warn(`Unknown block type: ${blockType}`);
    }
  }

  function handleDelete(document: Document, blockId: string) {
    if (!docs) return;
    docs.dropBlock(document, blockId);
  }

  function handleStartRename(document: Document) {
    setEditingName(document.name);
    setIsEditing(true);
  }

  function handleSaveRename(document: Document) {
    if (!docs || !editingName.trim()) return;
    docs.renameDocument(document.id, editingName.trim());
    setIsEditing(false);
  }

  function handleCancelRename() {
    setIsEditing(false);
    setEditingName("");
  }

  if (!id) {
    return (
      <ClosableCard title="Document Not Found">
        <CardContent>
          <p>No document ID provided.</p>
          <Button onClick={() => navigate("/documents")}>
            Back to Documents
          </Button>
        </CardContent>
      </ClosableCard>
    );
  }

  if (!tree || !tree.document) {
    return (
      <ClosableCard title="Loading...">
        <CardContent>
          <p>Loading document...</p>
        </CardContent>
      </ClosableCard>
    );
  }

  return (
    <Sidebar
      title={
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Input
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveRename(tree.document);
                  } else if (e.key === "Escape") {
                    handleCancelRename();
                  }
                }}
                className="text-lg font-semibold"
                autoFocus
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSaveRename(tree.document)}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCancelRename}>
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <span>{tree.document.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleStartRename(tree.document)}
                className="opacity-50 hover:opacity-100"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      }
    >
      <CardContent>
        {tree && (
          <div className="flex flex-row justify-between items-center">
            <div className="text-sm text-muted-foreground">
              Created: {new Date(tree.document.createdOn).toLocaleDateString()}
            </div>
            <AddBlockDropdown
              onAddBlock={(blockType) => handleCreate(tree.document, blockType)}
              allowedTypes={["feature", "point", "route", "remotegeojson"]}
            />
          </div>
        )}

        <div className="flex flex-col space-y-2">
          {tree.getDocumentRoot().map((block) => (
            <div
              className="flex items-center justify-between hover:bg-muted/50 p-4 rounded-lg"
              key={block.id}
            >
              <div className="flex-1">
                <Block block={block} tree={tree} />
              </div>
              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(tree.document, block.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {tree.getDocumentRoot().length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              No blocks in this document. Click "Add Block" to get started.
            </div>
          )}
        </div>
      </CardContent>
    </Sidebar>
  );
}
